/**
 * Platform Anomalies Tab - Admin Console
 * FR-ANOM-014: cross-tenant, platform-ops-only findings - deliberately a
 * separate view from the tenant-facing Anomalies page, since these rows
 * never belong to any one tenant's own anomaly list.
 */
import React, { useState, useEffect } from 'react';
import { getPlatformAnomalies, triggerPlatformAnomalyDetection, PlatformAnomaly } from '../../services/anomalyService';

export const PlatformAnomaliesTab: React.FC = () => {
  const [rows, setRows] = useState<PlatformAnomaly[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const data = await getPlatformAnomalies();
      setRows(data);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load platform anomalies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDetectNow = async () => {
    setTriggering(true);
    try {
      await triggerPlatformAnomalyDetection();
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to run platform-wide detection');
    } finally {
      setTriggering(false);
    }
  };

  const severityClass = (severity: string) => {
    if (severity === 'critical') return 'text-rose-700 dark:text-rose-400';
    if (severity === 'high') return 'text-amber-700 dark:text-amber-400';
    return 'text-foreground/80';
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="mb-1.5 text-sm font-semibold text-foreground">Platform-Wide Anomalies</h3>
            <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
              A (provider, service) flagged as anomalous for 2 or more independent tenants on the same day - real
              evidence of a platform/provider-wide cost event (a pricing change, a rate-limit incident, an outage),
              not something any one tenant caused. Runs automatically every hour at :20; you can also trigger it
              manually below.
            </p>
          </div>
          <button
            onClick={handleDetectNow}
            disabled={triggering}
            className="shrink-0 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground/80 hover:bg-muted/30 disabled:opacity-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-secondary"
          >
            {triggering ? 'Detecting...' : 'Detect Now'}
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No platform-wide anomalies found - either nothing cross-tenant has happened recently, or this deployment
            only has a handful of tenants whose usage patterns haven't happened to correlate yet. Both are expected,
            honest outcomes, not a sign anything is broken.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Date</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Provider</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Service</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Tenants Affected</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Severity</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border dark:divide-border">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2 text-sm text-muted-foreground">{new Date(r.detected_date).toLocaleDateString()}</td>
                    <td className="px-3 py-2 text-sm uppercase text-foreground">{r.provider}</td>
                    <td className="px-3 py-2 text-sm text-foreground">{r.service_name}</td>
                    <td className="px-3 py-2 text-sm text-muted-foreground">
                      {r.tenant_count_affected} / {r.total_tenant_count}
                    </td>
                    <td className={`px-3 py-2 text-sm font-medium uppercase ${severityClass(r.severity)}`}>{r.severity}</td>
                    <td className="px-3 py-2 text-sm text-muted-foreground capitalize">{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
